import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuPencil, LuTrash2, LuChevronLeft, LuChevronRight,
    LuFileText, LuVideo, LuExternalLink,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import ConfirmationModal from '../components/ConfirmationModal';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
// HelpManagerPage.css guarda classes que o editor de artigos consome —
// ver o aviso dentro do arquivo antes de mexer.
import './HelpManagerPage.css';
import { HelpService } from '../services/HelpService';
import type { HelpArticle, HelpCategory, HelpVideo } from '../types/Help';

const POR_PAGINA = [25, 50, 100];
type Aba = 'artigos' | 'videos';
type FiltroArtigo = 'todos' | 'publicado' | 'rascunho';

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

interface RascunhoVideo {
    id: number | null;
    titulo: string; descricao: string; youtubeUrl: string;
    duracao: string; ordem: number; ativo: boolean;
}
const VIDEO_VAZIO: RascunhoVideo = {
    id: null, titulo: '', descricao: '', youtubeUrl: '', duracao: '', ordem: 0, ativo: true,
};

const HelpManagerPage = () => {
    const celular = useCelular();
    const navigate = useNavigate();

    const [artigos, setArtigos] = useState<HelpArticle[]>([]);
    const [categorias, setCategorias] = useState<HelpCategory[]>([]);
    const [videos, setVideos] = useState<HelpVideo[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Esta tela SAI para o editor (/admin/help/edit/:id) e volta: e a que mais
       perdia o recorte, uma vez por artigo editado. */
    const [recorte, gravarRecorte] = useRecorte('aj:recorte', {
        aba: 'artigos' as Aba, busca: '', filtro: 'todos' as FiltroArtigo,
        pagina: 1, porPagina: 25,
    });
    const [aba, setAba] = useState<Aba>(recorte.aba);
    const [busca, setBusca] = useState(recorte.busca);
    const [filtro, setFiltro] = useState<FiltroArtigo>(recorte.filtro);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ aba, busca, filtro, pagina, porPagina });
    }, [aba, busca, filtro, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<HelpVideo | null>(null);
    const [rascunho, setRascunho] = useState<RascunhoVideo>(VIDEO_VAZIO);
    const [salvando, setSalvando] = useState(false);

    const [aExcluir, setAExcluir] = useState<{ tipo: Aba; id: number; nome: string } | null>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [arts, cats, vids] = await Promise.all([
                HelpService.getArticles().catch(() => []),
                HelpService.getCategories().catch(() => []),
                HelpService.getVideos().catch(() => []),
            ]);
            setArtigos(arts); setCategorias(cats); setVideos(vids);
        } catch {
            aviso.erro('Não foi possível carregar a central de ajuda.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, filtro, porPagina, aba]);

    const nomeCategoria = (id: number) => categorias.find(c => c.id === id)?.nome ?? '—';

    const contagens = useMemo(() => ({
        todos: artigos.length,
        publicado: artigos.filter(a => !a.isRascunho).length,
        rascunho: artigos.filter(a => a.isRascunho).length,
    }), [artigos]);

    const artigosFiltrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return artigos.filter(a => {
            if (filtro === 'publicado' && a.isRascunho) return false;
            if (filtro === 'rascunho' && !a.isRascunho) return false;
            if (!t) return true;
            return [a.titulo, a.tags, nomeCategoria(a.categoriaId)]
                .some(v => (v || '').toLowerCase().includes(t));
        });
    }, [artigos, busca, filtro, categorias]);

    const videosFiltrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        const ordenados = [...videos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        return t
            ? ordenados.filter(v => [v.titulo, v.descricao].some(x => (x || '').toLowerCase().includes(t)))
            : ordenados;
    }, [videos, busca]);

    const lista: (HelpArticle | HelpVideo)[] = aba === 'artigos' ? artigosFiltrados : videosFiltrados;
    const totalPaginas = Math.max(1, Math.ceil(lista.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = lista.slice(inicio, inicio + porPagina);
    /** A aba não tem nenhum registro — não é filtro. A porta de criar vai para o aviso. */
    const abaVazia = !isLoading && (aba === 'artigos' ? artigos.length : videos.length) === 0;

    /* ─── Vídeos ─────────────────────────────────────────────── */

    const abrirVideo = (v?: HelpVideo) => {
        setAberto(v ?? ({ id: 0 } as HelpVideo));
        setRascunho(v
            ? {
                id: v.id, titulo: v.titulo, descricao: v.descricao ?? '',
                youtubeUrl: v.youtubeUrl, duracao: v.duracao ?? '',
                ordem: v.ordem ?? 0, ativo: v.ativo,
            }
            : { ...VIDEO_VAZIO, ordem: videos.length });
    };

    const salvarVideo = async () => {
        if (!rascunho.titulo.trim()) { aviso.atencao('Informe o título do vídeo.'); return; }
        if (!rascunho.youtubeUrl.trim()) { aviso.atencao('Informe o link do YouTube.'); return; }
        setSalvando(true);
        try {
            const dto = {
                titulo: rascunho.titulo.trim(),
                descricao: rascunho.descricao.trim(),
                youtubeUrl: rascunho.youtubeUrl.trim(),
                duracao: rascunho.duracao.trim(),
                ordem: rascunho.ordem,
                ativo: rascunho.ativo,
            };
            if (rascunho.id) await HelpService.updateVideo(rascunho.id, dto);
            else await HelpService.createVideo(dto);
            aviso.ok(rascunho.id ? 'Vídeo atualizado.' : 'Vídeo criado.');
            setAberto(null);
            await carregar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o vídeo.'));
        } finally {
            setSalvando(false);
        }
    };

    const confirmarExclusao = async () => {
        if (!aExcluir) return;
        try {
            if (aExcluir.tipo === 'artigos') await HelpService.deleteArticle(aExcluir.id);
            else await HelpService.deleteVideo(aExcluir.id);
            aviso.ok(aExcluir.tipo === 'artigos' ? 'Artigo excluído.' : 'Vídeo excluído.');
            await carregar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível excluir.'));
        } finally {
            setAExcluir(null);
        }
    };

    const COL_ART = '2fr 1.1fr 1fr .9fr 106px';
    const COL_VID = '2fr 1.4fr .7fr .8fr 106px';
    const COLUNAS = aba === 'artigos' ? COL_ART : COL_VID;

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX.
       Os dois já valem para as duas abas: quem muda é `aba`, não a casca. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {aba === 'artigos' ? 'Nenhum artigo' : 'Nenhum vídeo'}
            </div>
            <p className="lk-blank-text">
                {aba === 'artigos'
                    ? 'Os artigos aparecem na central de ajuda que o usuário abre pelo menu.'
                    : 'Os vídeos aparecem na central de ajuda, na ordem definida aqui.'}
            </p>
            {abaVazia && (aba === 'artigos'
                ? <button type="button" className="lk-btn is-primary" onClick={() => navigate('/admin/help/new')}>
                    <LuPlus size={14} /> Novo artigo
                  </button>
                : <button type="button" className="lk-btn is-primary" onClick={() => abrirVideo()}>
                    <LuPlus size={14} /> Novo vídeo
                  </button>)}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, lista.length)} de {lista.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Central de ajuda"
                subtitle="Os artigos e vídeos que os usuários veem na ajuda."
                tabs={[
                    { id: 'artigos', label: 'Artigos', icon: <LuFileText size={14} /> },
                    { id: 'videos', label: 'Vídeos', icon: <LuVideo size={14} /> },
                ]}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                actions={abaVazia ? undefined :
                    <div className="lk-actions">
                        {aba === 'artigos' ? (
                            <button type="button" className="lk-btn is-primary" onClick={() => navigate('/admin/help/new')}>
                                <LuPlus size={14} /> Novo artigo
                            </button>
                        ) : (
                            <button type="button" className="lk-btn is-primary" onClick={() => abrirVideo()}>
                                <LuPlus size={14} /> Novo vídeo
                            </button>
                        )}
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search"
                            placeholder={aba === 'artigos' ? 'Buscar por título, tag ou categoria…' : 'Buscar por título…'}
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>

                    {aba === 'artigos'
                        ? ([['todos', 'Todos'], ['publicado', 'Publicados'], ['rascunho', 'Rascunhos']] as [FiltroArtigo, string][])
                            .map(([id, rotulo]) => (
                                <button key={id} type="button"
                                    className={`lk-pill ${filtro === id ? 'is-on' : ''}`}
                                    onClick={() => setFiltro(id)}>
                                    {rotulo} <b>{contagens[id]}</b>
                                </button>
                            ))
                        : <span className="lk-count">
                            {videos.length} {videos.length === 1 ? 'vídeo' : 'vídeos'}
                          </span>}
                </div>

                {/* Celular: as cinco colunas não cabem em 358px, então a linha vira
                    cartão, uma aba de cada vez. O toque faz o que o lápis faz — o
                    artigo vai para o editor, o vídeo abre o painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : aba === 'artigos'
                                    ? (visiveis as HelpArticle[]).map(a => (
                                        <ListCard
                                            key={a.id}
                                            titulo={a.titulo}
                                            apoio={nomeCategoria(a.categoriaId)}
                                            estado={
                                                <>
                                                    <span className={`rp-badge ${a.isRascunho ? 'is-open' : 'is-good'}`}>
                                                        {a.isRascunho ? 'Rascunho' : 'Publicado'}
                                                    </span>
                                                    <span>· {dataCurta(a.dataAtualizacao || a.dataCriacao)}</span>
                                                </>
                                            }
                                            label={`Editar ${a.titulo}`}
                                            onOpen={() => navigate(`/admin/help/edit/${a.id}`)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir artigo" danger
                                                    onClick={() => setAExcluir({ tipo: 'artigos', id: a.id, nome: a.titulo })} />
                                            }
                                        />
                                    ))
                                    : (visiveis as HelpVideo[]).map(v => (
                                        <ListCard
                                            key={v.id}
                                            titulo={v.titulo}
                                            apoio={v.descricao || v.youtubeUrl}
                                            estado={
                                                <>
                                                    <span className={`rp-badge ${v.ativo ? 'is-good' : 'is-lost'}`}>
                                                        {v.ativo ? 'Ativo' : 'Oculto'}
                                                    </span>
                                                    {v.duracao && <span>· {v.duracao}</span>}
                                                </>
                                            }
                                            selected={aberto?.id === v.id}
                                            label={`Editar ${v.titulo}`}
                                            onOpen={() => abrirVideo(v)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir vídeo" danger
                                                    onClick={() => setAExcluir({ tipo: 'videos', id: v.id, nome: v.titulo })} />
                                            }
                                        />
                                    ))}
                        </ListCards>
                        {!isLoading && lista.length === 0 && listaVazia}
                        {!isLoading && lista.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            {aba === 'artigos' ? (
                                <>
                                    <div className="lk-head">Título</div>
                                    <div className="lk-head">Categoria</div>
                                    <div className="lk-head">Atualizado</div>
                                    <div className="lk-head">Situação</div>
                                </>
                            ) : (
                                <>
                                    <div className="lk-head">Vídeo</div>
                                    <div className="lk-head">Link</div>
                                    <div className="lk-head">Duração</div>
                                    <div className="lk-head">Situação</div>
                                </>
                            )}
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>

                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : aba === 'artigos'
                                    /* Sem clique na linha e sem olho: o artigo abre numa PÁGINA de
                                       edição, não num painel — não há modo "ver" para o olho
                                       apontar. Ver `padrao-de-tela.md`. */
                                    ? (visiveis as HelpArticle[]).map(a => (
                                        <ListRow key={a.id} columns={COLUNAS}>
                                            <span className="lk-cell lk-strong"><span>{a.titulo}</span></span>
                                            <span className="lk-cell"><span className="lk-tag">{nomeCategoria(a.categoriaId)}</span></span>
                                            <span className="lk-cell lk-muted">
                                                <span>{dataCurta(a.dataAtualizacao || a.dataCriacao)}</span>
                                            </span>
                                            <span className="lk-cell">
                                                <span className={`rp-badge ${a.isRascunho ? 'is-open' : 'is-good'}`}>
                                                    {a.isRascunho ? 'Rascunho' : 'Publicado'}
                                                </span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuPencil size={14} />} label="Editar artigo"
                                                    onClick={() => navigate(`/admin/help/edit/${a.id}`)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir artigo" danger
                                                    onClick={() => setAExcluir({ tipo: 'artigos', id: a.id, nome: a.titulo })} />
                                            </RowActions>
                                        </ListRow>
                                    ))
                                    : (visiveis as HelpVideo[]).map(v => (
                                        <ListRow key={v.id} columns={COLUNAS}
                                            selected={aberto?.id === v.id}>
                                            <span className="lk-cell lk-strong"><span>{v.titulo}</span></span>
                                            <span className="lk-cell lk-muted hm-link"><span>{v.youtubeUrl}</span></span>
                                            <span className={`lk-cell ${v.duracao ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{v.duracao || '—'}</span>
                                            </span>
                                            <span className="lk-cell">
                                                <span className={`rp-badge ${v.ativo ? 'is-good' : 'is-lost'}`}>
                                                    {v.ativo ? 'Ativo' : 'Oculto'}
                                                </span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuPencil size={14} />} label="Editar vídeo"
                                                    onClick={() => abrirVideo(v)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir vídeo" danger
                                                    onClick={() => setAExcluir({ tipo: 'videos', id: v.id, nome: v.titulo })} />
                                            </RowActions>
                                        </ListRow>
                                    ))}
                        </div>
                    </div>

                    {!isLoading && lista.length === 0 && listaVazia}
                    {!isLoading && lista.length > 0 && rodape}
                </div>
                )}
            </div>

            {/* Artigo tem editor próprio (/admin/help/edit/:id); só o vídeo
                cabe num painel, porque é um punhado de campos. */}
            <RecordPanel
                open={!!aberto}
                onClose={() => setAberto(null)}
                title={rascunho.titulo.trim() || (rascunho.id ? 'Vídeo' : 'Novo vídeo')}
                editing
                footer={
                    <>
                        <button type="button" className="rp-btn" onClick={() => setAberto(null)} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="button" className="rp-btn is-primary" onClick={salvarVideo} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                <PanelSection title="Vídeo">
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="hm-tit">Título</label>
                        <input id="hm-tit" className="rp-input" type="text" autoFocus
                            value={rascunho.titulo}
                            onChange={e => setRascunho(r => ({ ...r, titulo: e.target.value }))} />
                    </div>

                    <div className="rp-field">
                        <label className="rp-label" htmlFor="hm-url">Link do YouTube</label>
                        <input id="hm-url" className="rp-input" type="url"
                            value={rascunho.youtubeUrl} placeholder="https://youtube.com/watch?v=…"
                            onChange={e => setRascunho(r => ({ ...r, youtubeUrl: e.target.value }))} />
                        {rascunho.youtubeUrl && (
                            <a className="hm-ver" href={rascunho.youtubeUrl} target="_blank" rel="noreferrer">
                                <LuExternalLink size={12} /> Abrir o vídeo
                            </a>
                        )}
                    </div>

                    <div className="rp-field">
                        <label className="rp-label" htmlFor="hm-desc">Descrição</label>
                        <input id="hm-desc" className="rp-input" type="text"
                            value={rascunho.descricao}
                            onChange={e => setRascunho(r => ({ ...r, descricao: e.target.value }))} />
                    </div>

                    <div className="rp-two">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="hm-dur">Duração</label>
                            <input id="hm-dur" className="rp-input" type="text"
                                value={rascunho.duracao} placeholder="Ex.: 4:32"
                                onChange={e => setRascunho(r => ({ ...r, duracao: e.target.value }))} />
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="hm-ord">Ordem</label>
                            <input id="hm-ord" className="rp-input" type="number" min={0}
                                value={rascunho.ordem}
                                onChange={e => setRascunho(r => ({ ...r, ordem: Math.max(0, Number(e.target.value) || 0) }))} />
                            <span className="hm-hint">Posição na central de ajuda.</span>
                        </div>
                    </div>

                    <div className="rp-toggle-row">
                        <div className="rp-toggle-text">
                            <span className="rp-toggle-label">Visível</span>
                            <span className="rp-toggle-hint">Oculto, o vídeo some da central de ajuda.</span>
                        </div>
                        <Switch
                            checked={rascunho.ativo}
                            label="Vídeo visível"
                            onChange={v => setRascunho(r => ({ ...r, ativo: v }))}
                        />
                    </div>
                </PanelSection>
            </RecordPanel>

            <ConfirmationModal
                isOpen={!!aExcluir}
                onClose={() => setAExcluir(null)}
                onConfirm={confirmarExclusao}
                title={aExcluir?.tipo === 'artigos' ? 'Excluir artigo' : 'Excluir vídeo'}
                message={`Tem certeza que deseja excluir "${aExcluir?.nome}"?`}
            />
        </div>
    );
};

export default HelpManagerPage;
